import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatCurrency } from '../../utils/formatters';

import { useTrialBalance } from '@/hooks/react-query/useGeneralLedger';
import { useMemo } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';

interface TrialBalanceTabProps {
  fundId: string;
}

function formatBalanceWithSign(value: number): {
  text: string;
  isNegative: boolean;
} {
  const isNegative = value < 0;
  const formatted = formatCurrency(Math.abs(value));
  return {
    text: isNegative ? `(${formatted})` : formatted,
    isNegative,
  };
}

export function TrialBalanceTab({ fundId }: TrialBalanceTabProps) {
  const trialBalanceParams = useMemo(
    () => ({
      fund_id: fundId,
    }),
    [fundId]
  );

  const trialBalanceQuery = useTrialBalance(trialBalanceParams);
  const isLoading = trialBalanceQuery.isLoading;
  const trialBalance = trialBalanceQuery.data;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        Loading trial balance...
      </div>
    );
  }

  if (!trialBalance) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <BarChart3 className="h-12 w-12 mx-auto mb-4" />
        No trial balance data available
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="uppercase text-xs font-semibold text-muted-foreground">
              Account Code
            </TableHead>
            <TableHead className="uppercase text-xs font-semibold text-muted-foreground">
              Account Name
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Net Balance (EUR)
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {trialBalance.accounts.map((account) => {
            const { text, isNegative } = formatBalanceWithSign(
              account.net_balance
            );
            return (
              <TableRow key={account.account_code}>
                <TableCell className="font-medium">
                  {account.account_code}
                </TableCell>
                <TableCell>{account.account_name}</TableCell>
                <TableCell
                  className={`text-right ${isNegative ? 'text-red-500' : ''}`}
                >
                  {text}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
